import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ValidDate } from "../../../../../utils/DateUtils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";
import { useSelector } from "react-redux";
// import { fetchLeads } from "../../../../redux/slices/leadsSlice";
// import RemoveIcon from '@mui/icons-material/Remove';
import { Calendar, ChevronDown } from "lucide-react";
import { hasViewOrFullAccess } from "../../../../../utils/featureAccessUtils";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

const normalizeStatus = (status) => String(status ?? "").trim().toLowerCase();

/**
 * Year for conversion math — same as Lead Tracker / DashFilters:
 * `new Date(lead.LeadDate).getFullYear()`.
 * Missing/invalid dates are parked in `fallbackYear` so they are still counted
 * (status cards count them; dropping them caused 80/105=76.19% instead of 81/108=75%).
 */
const getLeadYear = (dateString, fallbackYear = null) => {
    if (dateString === null || dateString === undefined || dateString === "") {
        return fallbackYear != null ? String(fallbackYear) : null;
    }
    const d = new Date(dateString);
    if (isNaN(d.getTime())) {
        return fallbackYear != null ? String(fallbackYear) : null;
    }
    return String(d.getFullYear());
};

const isWonStatus = (status) => normalizeStatus(status) === "won";
const isWonRecurringStatus = (status) =>
    normalizeStatus(status) === "won recurring";

/** won|wonRecurring count / all leads in year * 100 — same formula as Lead Tracker. */
const getConversionPctForYear = (leads, year, statusMatcher, fallbackYear) => {
    const yearKey = String(year);
    let total = 0;
    let matched = 0;
    (leads || []).forEach((lead) => {
        const leadYear = getLeadYear(lead?.LeadDate, fallbackYear);
        if (leadYear !== yearKey) return;
        total += 1;
        if (statusMatcher(lead?.Status)) matched += 1;
    });
    if (total <= 0) return "0";
    const pct = (matched / total) * 100;
    // Prefer whole numbers when exact (e.g. 81/108 → 75)
    return Number.isInteger(pct) || Math.abs(pct - Math.round(pct)) < 1e-9
        ? String(Math.round(pct))
        : pct.toFixed(2);
};

const getPercentageByYear = (leads, statusMatcher, fallbackYear) => {
    const statusCountByYear = {};
    const totalLeadsByYear = {};

    (leads || []).forEach((lead) => {
        const year = getLeadYear(lead?.LeadDate, fallbackYear);
        if (!year) return;

        if (!totalLeadsByYear[year]) totalLeadsByYear[year] = 0;
        totalLeadsByYear[year]++;

        if (statusMatcher(lead?.Status)) {
            if (!statusCountByYear[year]) statusCountByYear[year] = 0;
            statusCountByYear[year]++;
        }
    });

    const percentageByYear = {};
    Object.keys(totalLeadsByYear).forEach((year) => {
        const statusCount = statusCountByYear[year] || 0;
        const total = totalLeadsByYear[year] || 0;
        if (total <= 0) {
            percentageByYear[year] = "0";
            return;
        }
        const pct = (statusCount / total) * 100;
        percentageByYear[year] =
            Number.isInteger(pct) || Math.abs(pct - Math.round(pct)) < 1e-9
                ? String(Math.round(pct))
                : pct.toFixed(2);
    });

    return percentageByYear;
};

const LeadConversionRate = ({ currentEmployee, hasNoFeatureConfig, userLevel }) => {
    const { filteredReports } = useSelector((state) => state.filteredData);

    const canShowGraphs = userLevel === "L0" || hasNoFeatureConfig || !currentEmployee || hasViewOrFullAccess(currentEmployee, "LEAD CONVERSION RATE GRAPHS");

    const [LeadReports, setLeadReports] = useState([]);
    const dropdownRef = useRef(null);
    const MonthdropdownRef = useRef(null);
    const [showYearDropdown, setShowYearDropdown] = useState(false);
    const [, setShowMonthDropdown] = useState(false);
    const [, setIsLoading] = useState(true);
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [wonLeads, setwonLeads] = useState([]);
    const [filteredWonLeads, setFilteredWonLeads] = useState([]);
    const [wonRecurring, setWonRecurring] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [mainData, setMainData] = useState([]);


    console.log("wonLeads", wonLeads)

    // const {leads} = useSelector((state)=> state.leads)
    const [showgraph, setShowgraph] = useState(true);

    const handleToggleTable = () => {
        setShowgraph(!showgraph);
    };
    useEffect(() => {
        if (Array.isArray(filteredReports)) {
            setMainData(filteredReports);
        }
    }, [filteredReports]);

    useEffect(() => {
        if (!Array.isArray(mainData) || mainData.length === 0) {
            setLeadReports([]);
            setWonRecurring([]);
            setwonLeads([]);
            return;
        }

        // Keep original LeadDate so year bucketing matches Lead Tracker
        // (new Date(lead.LeadDate).getFullYear()). Do not rewrite via ValidDate/UTC.
        const formattedData = Array.isArray(mainData) ? [...mainData] : [];

        const wonRecurringLeads = formattedData.filter(
            (lead) => normalizeStatus(lead.Status) === "won recurring"
        );

        const wonLeadsData = formattedData.filter(
            (lead) => normalizeStatus(lead.Status) === "won"
        );

        setLeadReports(formattedData);
        setWonRecurring(wonRecurringLeads);
        setwonLeads(wonLeadsData);
    }, [mainData]);






    const pastFiveYears = useMemo(
        () => Array.from({ length: 6 }, (_, i) => (currentYear - 5 + i).toString()),
        [currentYear]
    );

    // Source of truth = same filteredReports as Lead Tracker status cards
    const conversionLeads = useMemo(
        () => (Array.isArray(filteredReports) ? filteredReports : LeadReports),
        [filteredReports, LeadReports]
    );

    const formatPct = (pct) => {
        if (!Number.isFinite(pct)) return "0";
        return Number.isInteger(pct) || Math.abs(pct - Math.round(pct)) < 1e-9
            ? String(Math.round(pct))
            : pct.toFixed(2);
    };

    const wonPercentageByYear = useMemo(
        () => getPercentageByYear(conversionLeads, isWonStatus, currentYear),
        [conversionLeads, currentYear]
    );

    const wonRurringPercentageByYear = useMemo(
        () =>
            getPercentageByYear(conversionLeads, isWonRecurringStatus, currentYear),
        [conversionLeads, currentYear]
    );

    /**
     * HARD card %:
     * - Current year: exact Lead Tracker formula → won / all filteredReports * 100
     *   (81/108 = 75%; avoids dropping undated leads that caused 76.19%)
     * - Other years: won(year) / all(year) * 100 with undated parked in current year
     */
    const wonPct = useMemo(() => {
        if (Number(selectedYear) === Number(currentYear)) {
            const total = conversionLeads.length;
            if (!total) return "0";
            const won = conversionLeads.filter((l) => isWonStatus(l.Status)).length;
            return formatPct((won / total) * 100);
        }
        return getConversionPctForYear(
            conversionLeads,
            selectedYear,
            isWonStatus,
            currentYear
        );
    }, [conversionLeads, selectedYear, currentYear]);

    const wonRecPct = useMemo(() => {
        if (Number(selectedYear) === Number(currentYear)) {
            const total = conversionLeads.length;
            if (!total) return "0";
            const wonRec = conversionLeads.filter((l) =>
                isWonRecurringStatus(l.Status)
            ).length;
            return formatPct((wonRec / total) * 100);
        }
        return getConversionPctForYear(
            conversionLeads,
            selectedYear,
            isWonRecurringStatus,
            currentYear
        );
    }, [conversionLeads, selectedYear, currentYear]);

    const percentageChange = useMemo(
        () =>
            pastFiveYears.map((year) => {
                const prevYear = (parseInt(year, 10) - 1).toString();
                const currentPct =
                    Number(year) === Number(selectedYear)
                        ? parseFloat(wonPct)
                        : parseFloat(wonPercentageByYear[year]);
                const prevPct = parseFloat(wonPercentageByYear[prevYear]);
                if (Number.isFinite(currentPct) && Number.isFinite(prevPct)) {
                    return (currentPct - prevPct).toFixed(2);
                }
                return "0";
            }),
        [pastFiveYears, wonPercentageByYear, wonPct, selectedYear]
    );

    const percentageChangeWonRecurring = useMemo(
        () =>
            pastFiveYears.map((year) => {
                const prevYear = (parseInt(year, 10) - 1).toString();
                const currentPct =
                    Number(year) === Number(selectedYear)
                        ? parseFloat(wonRecPct)
                        : parseFloat(wonRurringPercentageByYear[year]);
                const prevPct = parseFloat(wonRurringPercentageByYear[prevYear]);
                if (Number.isFinite(currentPct) && Number.isFinite(prevPct)) {
                    return (currentPct - prevPct).toFixed(2);
                }
                return "0";
            }),
        [pastFiveYears, wonRurringPercentageByYear, wonRecPct, selectedYear]
    );

    const selectedYearChange =
        percentageChange[pastFiveYears.indexOf(selectedYear.toString())] || "0";
    const selectedYearChangeWonRecurring =
        percentageChangeWonRecurring[
            pastFiveYears.indexOf(selectedYear.toString())
        ] || "0";

    // Keep chart point for selected year aligned with the hard card %
    const wonPercentageByYearForChart = useMemo(() => {
        const next = { ...wonPercentageByYear };
        next[String(selectedYear)] = wonPct;
        return next;
    }, [wonPercentageByYear, selectedYear, wonPct]);

    const wonRecurringPercentageByYearForChart = useMemo(() => {
        const next = { ...wonRurringPercentageByYear };
        next[String(selectedYear)] = wonRecPct;
        return next;
    }, [wonRurringPercentageByYear, selectedYear, wonRecPct]);

    useEffect(() => {
        if (selectedYear) {
            const filterWonLeadsByYear = wonLeads.filter((lead) => {
                const leadYear = getLeadYear(lead?.LeadDate, currentYear);
                return leadYear === selectedYear.toString();
            });
            setFilteredWonLeads(filterWonLeadsByYear);
        }
    }, [selectedYear, wonLeads, currentYear]);

    useEffect(() => { }, [wonLeads, wonRecurring, chartData, filteredWonLeads]);
    // Handle clicks outside the dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowYearDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                MonthdropdownRef.current &&
                !MonthdropdownRef.current.contains(event.target)
            ) {
                setShowMonthDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (LeadReports.length > 0) {
            setIsLoading(false);
        }
    }, [LeadReports]);
    // Last 6 years for dropdown: current year and 5 years back (e.g. 2021 to 2026)
    const years = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

    useEffect(() => {
        const processData = () => {
            const monthlyCounts = {};

            filteredWonLeads.forEach((lead) => {
                if (!lead.LeadDate) {
                    console.warn("Missing LeadDate for lead:", lead);
                    return;
                }

                const normalized = ValidDate(lead.LeadDate);
                if (!normalized) {
                    console.warn("Invalid date format:", lead.LeadDate);
                    return;
                }

                const [year, month, day] = normalized.split("-");
                const formattedDate = new Date(`${year}-${month}-${day}`);

                if (isNaN(formattedDate.getTime())) {
                    console.warn("Invalid date format:", lead.LeadDate);
                    return;
                }

                // Extract month (e.g., "Jan", "Feb", "Mar")
                const monthName = formattedDate.toLocaleString("en-US", {
                    month: "short",
                });

                if (!monthlyCounts[monthName]) {
                    monthlyCounts[monthName] = 0;
                }
                monthlyCounts[monthName]++;
            });

            const totalLeads = filteredWonLeads.length;
            const formattedData = Object.keys(monthlyCounts).map((month) => ({
                month,
                percentage: ((monthlyCounts[month] / totalLeads) * 100).toFixed(2),
            }));

            setChartData(formattedData);
        };

        if (filteredWonLeads.length > 0) {
            processData();
        }
    }, [filteredWonLeads]);

    //   const data = linedata.map((item) => {
    //     const found = chartData.find((entry) => entry.month === item.name);
    //     return {
    //       ...item,
    //       value: found ? parseFloat(found.percentage) : 0, // Convert percentage to number
    //     };
    //   });

    const chartRef1 = useRef(null);
    const chartRef2 = useRef(null);
    const [chartLoaded, setChartLoaded] = useState(false);

    const [hasDataChart1, setHasDataChart1] = useState(true);
    const [hasDataChart2, setHasDataChart2] = useState(true);

    useEffect(() => {
        if (!window.google) {
            const script = document.createElement("script");
            script.src = "https://www.gstatic.com/charts/loader.js";
            script.async = true;
            script.onload = () => {
                window.google.charts.load("current", {
                    packages: ["corechart", "line"],
                });
                window.google.charts.setOnLoadCallback(() => setChartLoaded(true));
            };
            document.body.appendChild(script);
        } else {
            window.google.charts.load("current", { packages: ["corechart", "line"] });
            window.google.charts.setOnLoadCallback(() => setChartLoaded(true));
        }
    }, []);

    const drawChart = useCallback(
        (chartRef, dataValues, label, color, setHasData) => {
            if (
                !chartLoaded ||
                !window.google ||
                !window.google.visualization ||
                !chartRef.current
            ) {
                console.error("Google Charts is not available yet.");
                return;
            }

            // Last 6 years: current year and 5 years back (e.g. 2021 to 2026)
            const chartYears = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

            const data = new window.google.visualization.DataTable();
            data.addColumn("number", "Year");
            data.addColumn("number", label);

            const rows = chartYears.map((year) => [
                parseInt(year.toString().slice(-2)),
                dataValues?.[year] !== undefined ? parseFloat(dataValues[year]) : 0,
            ]);
            const hasValidData = rows.some((row) => row[1] > 0);

            setHasData(hasValidData); // Update state to track if data exists

            if (!hasValidData) {
                chartRef.current.innerHTML = "";
                return;
            }

            data.addRows(rows);

            const el = chartRef.current;
            const w = Math.max(el.clientWidth || el.offsetWidth || 160, 120);
            const h = Math.max(el.clientHeight || el.offsetHeight || 224, 200);
            const padL = 48;
            const padR = 8;
            const padT = 8;
            const padB = 34;

            const vTicks = [
                { v: 0, f: "0%" },
                { v: 25, f: "25%" },
                { v: 50, f: "50%" },
                { v: 75, f: "75%" },
                { v: 100, f: "100%" },
            ];

            const options = {
                width: w,
                height: h,
                legend: "none",
                colors: [color],
                fontName: "system-ui, sans-serif",
                curveType: "function",
                lineWidth: 2,
                pointSize: 4,
                series: {
                    0: { areaOpacity: 0.16 },
                },
                chartArea: {
                    left: padL,
                    top: padT,
                    width: w - padL - padR,
                    height: h - padT - padB,
                    backgroundColor: "transparent",
                },
                backgroundColor: { fill: "transparent" },
                hAxis: {
                    baselineColor: "#e5e7eb",
                    gridlines: { color: "transparent", count: 0 },
                    minorGridlines: { color: "transparent" },
                    textStyle: { color: "#6b7280", bold: false, fontSize: 11 },
                    // Display full years on axis (2021, 2022...) while keeping numeric domain stable.
                    // If underlying values are 2-digit (21,22...), format them to 4-digit labels.
                    ticks: chartYears.map((year) => {
                        const full = String(year);
                        const v = parseInt(full.slice(-2), 10);
                        return { v, f: full };
                    }),
                    slantedText: false,
                    textPosition: "out",
                    title: "Year",
                    titleTextStyle: { color: "#6b7280", fontSize: 11 },
                },
                vAxis: {
                    textPosition: "out",
                    textStyle: { color: "#6b7280", fontSize: 11 },
                    ticks: vTicks,
                    gridlines: { color: "#e5e7eb" },
                    minorGridlines: { color: "transparent" },
                    baselineColor: "#e5e7eb",
                    viewWindow: { min: 0, max: 100 },
                },
                tooltip: { trigger: "focus" },
            };

            const chart = new window.google.visualization.AreaChart(el);
            chart.draw(data, options);
        },
        [chartLoaded, currentYear]
    );

    useEffect(() => {
        if (chartLoaded) {
            drawChart(
                chartRef1,
                wonPercentageByYearForChart,
                "Won %",
                "#16a34a",
                setHasDataChart1
            );
            drawChart(
                chartRef2,
                wonRecurringPercentageByYearForChart,
                "Won Recurring %",
                "#65a30d",
                setHasDataChart2
            );
        }
    }, [
        chartLoaded,
        drawChart,
        wonPercentageByYearForChart,
        wonRecurringPercentageByYearForChart,
    ]);

    const wonChangeNum = Number(selectedYearChange);
    const wonRecChangeNum = Number(selectedYearChangeWonRecurring);

    return (
        <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex shrink-0 items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                    <button
                        type="button"
                        onClick={() => handleToggleTable()}
                        className="flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 transition hover:bg-gray-50"
                        aria-expanded={showgraph}
                        aria-label={showgraph ? "Collapse" : "Expand"}
                    >
                        <ChevronDown
                            className={`h-4 w-4 text-gray-600 ${showgraph ? "" : "rotate-180"}`}
                            strokeWidth={2}
                        />
                    </button>
                    <h1 className="dashboard-card-title min-w-0 truncate">
                        LEAD CONVERSION RATE
                    </h1>
                </div>
                <div className="h-9 w-9 shrink-0" aria-hidden />
            </div>

            {showgraph && canShowGraphs && (
                <div className="mt-2 flex min-h-0 flex-1 flex-col justify-between gap-2">
                    <div className="flex shrink-0 flex-col gap-2">
                    {(userLevel === "L0" ||
                        hasNoFeatureConfig ||
                        !currentEmployee ||
                        hasViewOrFullAccess(currentEmployee, "LEAD CONVERSION RATE FILTER")) && (
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                Select Year
                            </span>
                            <div className="flex flex-wrap items-center gap-3">
                                <div ref={dropdownRef} className="relative min-w-0">
                                    <button
                                        type="button"
                                        onClick={() => setShowYearDropdown(!showYearDropdown)}
                                        className="inline-flex h-10 min-w-[10rem] items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 text-left text-sm font-normal leading-none text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                    >
                                        <span className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4 shrink-0 text-gray-700" strokeWidth={2} />
                                            {selectedYear}
                                        </span>
                                        <ExpandMoreIcon sx={{ color: "#374151" }} fontSize="small" />
                                    </button>
                                    {showYearDropdown && (
                                        <div className="absolute left-0 top-full z-50 mt-1 max-h-48 min-w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg">
                                            {years
                                                .slice()
                                                .sort((a, b) => b - a)
                                                .map((year) => (
                                                    <label
                                                        key={year}
                                                        className="flex cursor-pointer items-center gap-2 px-4 py-2 hover:bg-gray-50"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            value={year}
                                                            checked={selectedYear === year}
                                                            onChange={(e) =>
                                                                setSelectedYear(parseInt(e.target.value, 10))
                                                            }
                                                            className="rounded border-gray-300"
                                                        />
                                                        {year}
                                                    </label>
                                                ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowYearDropdown(false)}
                                    className="inline-flex h-10 shrink-0 items-center justify-center rounded-md border-2 border-gray-700 bg-white px-5 text-xs font-semibold leading-none text-[#1F2937] tracking-widest"
                                >
                                    SUBMIT
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="grid shrink-0 grid-cols-2 gap-3">
                        <div className="flex min-w-0 items-center justify-between gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2">
                            <div className="min-w-0">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Won</p>
                                <p className="mt-0.5 text-lg font-semibold leading-tight text-green-600">{wonPct}%</p>
                            </div>
                            <div className="shrink-0 text-right">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    % Change
                                </p>
                                <p
                                    className={`mt-0.5 text-sm font-bold leading-tight ${
                                        wonChangeNum < 0
                                            ? "text-red-500"
                                            : wonChangeNum > 0
                                              ? "text-green-600"
                                              : "text-gray-900"
                                    }`}
                                >
                                    {selectedYearChange}%
                                </p>
                            </div>
                        </div>
                        <div className="flex min-w-0 items-center justify-between gap-2 rounded-xl border border-lime-200 bg-lime-50 px-3 py-2">
                            <div className="min-w-0">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Won Recurring
                                </p>
                                <p className="mt-0.5 text-lg font-semibold leading-tight text-lime-600">{wonRecPct}%</p>
                            </div>
                            <div className="shrink-0 text-right">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    % Change
                                </p>
                                <p
                                    className={`mt-0.5 text-sm font-bold leading-tight ${
                                        wonRecChangeNum < 0
                                            ? "text-red-500"
                                            : wonRecChangeNum > 0
                                              ? "text-green-600"
                                              : "text-gray-900"
                                    }`}
                                >
                                    {selectedYearChangeWonRecurring}%
                                </p>
                            </div>
                        </div>
                    </div>
                    </div>

                    <div className="mt-3 grid min-h-0 shrink-0 grid-cols-2 gap-3">
                        <div className="flex min-w-0 flex-col rounded-xl border border-gray-200 px-2 pb-1 pt-1.5">
                            <p className="mb-1 text-center text-xs font-medium text-gray-600">
                                HISTORICAL TREND — WON
                            </p>
                            <div className="relative h-56 w-full">
                                <div ref={chartRef1} className="h-56 w-full" />
                                {!hasDataChart1 && (
                                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/90">
                                        <p className="text-sm font-medium text-red-400">No data available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex min-w-0 flex-col rounded-xl border border-gray-200 px-2 pb-1 pt-1.5">
                            <p className="mb-1 text-center text-xs font-medium text-gray-600">
                                HISTORICAL TREND — WON RECURRING
                            </p>
                            <div className="relative h-56 w-full">
                                <div ref={chartRef2} className="h-56 w-full" />
                                {!hasDataChart2 && (
                                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/90">
                                        <p className="text-sm font-medium text-red-400">No data available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LeadConversionRate